import * as Yup from "yup";
import { validationMessages } from "../common/validationMessages";

export const SERVICE_DAY_KEYS = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
] as const;

export type ServiceDayKey = (typeof SERVICE_DAY_KEYS)[number];

export const SERVICE_DAY_LABELS: Record<ServiceDayKey, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

/** Matches backend CoverageStatus enum */
export type CoverageStatus = "SERVICED" | "NOT_SERVICED";

export const COVERAGE_STATUS_OPTIONS = [
  { label: "Serviced", value: "SERVICED" },
  { label: "Not Serviced", value: "NOT_SERVICED" },
];

export const DIRECT_FILTER_OPTIONS = [
  { label: "Direct", value: "true" },
  { label: "Indirect", value: "false" },
];

export const EMPTY_SELECT_VALUE = "";

export const ROUTE_GUIDE_FIELD_LABELS = {
  threePlOriginCode: "3PL origin",
  threePlDestCode: "3PL destination",
  extensionCode1: "Routing code 1",
  extensionCode2: "Routing code 2",
} as const;

export const ROUTE_GUIDE_FIELD_HELP = {
  threePlOriginCode:
    "3PL lane for origin (AS400 ZD3PLO). D = Direct, I = Indirect; blank = none.",
  threePlDestCode:
    "3PL lane for destination (AS400 ZD3PLD). D = Direct, I = Indirect; blank = none.",
  extensionCode1: "Optional routing code (AS400 ZDX1), e.g. BGL.",
  extensionCode2: "Optional routing code (AS400 ZDX2), e.g. Ferry, Remote, New York.",
} as const;

export const THREE_PL_LANE_OPTIONS = [
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "Direct (D)", value: "D" },
  { label: "Indirect (I)", value: "I" },
];

export const EXTENSION_CODE_1_OPTIONS = [
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "BGL", value: "BGL" },
];

export const EXTENSION_CODE_2_OPTIONS = [
  { label: "—", value: EMPTY_SELECT_VALUE },
  { label: "FERRY", value: "FERRY" },
  { label: "REMOTE", value: "REMOTE" },
  { label: "New York", value: "NEWYORK" },
];

/** List/detail row for GET /zip-routing-profiles */
export interface ZipRoutingProfile {
  id: string;
  zipCode?: string;
  zip?: string;
  state: string;
  city?: string | null;
  terminalNumber: number;
  airportCode?: string | null;
  terminalName?: string | null;
  coverageStatus?: CoverageStatus | string | null;
  rateType?: string | null;
  monday?: boolean;
  tuesday?: boolean;
  wednesday?: boolean;
  thursday?: boolean;
  friday?: boolean;
  saturday?: boolean;
  sunday?: boolean;
  serviceMon?: boolean;
  serviceTue?: boolean;
  serviceWed?: boolean;
  serviceThu?: boolean;
  serviceFri?: boolean;
  serviceSat?: boolean;
  serviceSun?: boolean;
  direct?: boolean;
  quoteAllowed?: boolean;
  threePlOriginCode?: string | null;
  threePlDestCode?: string | null;
  extensionCode1?: string | null;
  extensionCode2?: string | null;
  dateUpdated?: string | null;
  userUpdated?: string | null;
}

export type ZipRoutingProfileFormModel = {
  id?: string;
  zipCode: string;
  state: string;
  city: string;
  terminalNumber: number | null;
  coverageStatus: CoverageStatus;
  monday: boolean;
  tuesday: boolean;
  wednesday: boolean;
  thursday: boolean;
  friday: boolean;
  saturday: boolean;
  sunday: boolean;
  direct: boolean;
  quoteAllowed: boolean;
  threePlOriginCode: string;
  threePlDestCode: string;
  extensionCode1: string;
  extensionCode2: string;
};

export interface ZipRoutingProfileListParams {
  search?: string;
  state?: string;
  terminalNumber?: string;
  direct?: string;
  page?: number;
  size?: number;
}

export type ZipRoutingProfileActivityLogAction = "CREATE" | "UPDATE" | "DELETE";

/** Cross-profile activity log row from GET /zip-routing-profiles/activity-log/all */
export interface ZipRoutingProfileActivityLogModel {
  changedAt: string;
  modifiedBy: string;
  action: ZipRoutingProfileActivityLogAction | string;
  changeDescription: string;
  profileId: string;
  zipCode: string | null;
  state: string | null;
  terminalNumber: number | null;
  airportCode?: string | null;
  terminalName?: string | null;
}

export interface ZipRoutingProfileActivityLogFilters {
  search?: string;
  action?: string | null;
  dateFrom?: string | null;
  dateTo?: string | null;
}

export const ZIP_ROUTING_ACTIVITY_LOG_ACTION_OPTIONS = [
  { id: "CREATE", name: "CREATE" },
  { id: "UPDATE", name: "UPDATE" },
  { id: "DELETE", name: "DELETE" },
];

const normalizeCoverageStatus = (value?: string | null): CoverageStatus =>
  value === "NOT_SERVICED" ? "NOT_SERVICED" : "SERVICED";

const resolveDirect = (profile: ZipRoutingProfile): boolean => {
  if (typeof profile.direct === "boolean") return profile.direct;
  if (profile.rateType === "I") return false;
  if (profile.rateType === "D") return true;
  return true;
};

/** Form stores "" for empty; API uses null */
export const normalizeOptionalCode = (value?: string | null): string =>
  value?.trim() || EMPTY_SELECT_VALUE;

export const formatThreePlLaneCode = (code?: string | null): string => {
  if (!code?.trim()) return "—";
  if (code === "D") return "Direct";
  if (code === "I") return "Indirect";
  return code;
};

export const formatExtensionCode = (code?: string | null): string => {
  if (!code?.trim()) return "—";
  if (code === "NEWYORK") return "New York";
  return code.trim();
};

export const formatQuoteAllowed = (allowed?: boolean): string => (allowed ? "Yes" : "No");

export const emptyZipRoutingProfileForm = (): ZipRoutingProfileFormModel => ({
  zipCode: "",
  state: "",
  city: "",
  terminalNumber: null,
  coverageStatus: "SERVICED",
  monday: true,
  tuesday: true,
  wednesday: true,
  thursday: true,
  friday: true,
  saturday: false,
  sunday: false,
  direct: true,
  quoteAllowed: false,
  threePlOriginCode: EMPTY_SELECT_VALUE,
  threePlDestCode: EMPTY_SELECT_VALUE,
  extensionCode1: EMPTY_SELECT_VALUE,
  extensionCode2: EMPTY_SELECT_VALUE,
});

export const mapProfileToForm = (profile: ZipRoutingProfile): ZipRoutingProfileFormModel => ({
  id: profile.id,
  zipCode: profile.zipCode || profile.zip || "",
  state: profile.state || "",
  city: profile.city || "",
  terminalNumber: profile.terminalNumber ?? null,
  coverageStatus: normalizeCoverageStatus(profile.coverageStatus),
  monday: !!(profile.monday ?? profile.serviceMon),
  tuesday: !!(profile.tuesday ?? profile.serviceTue),
  wednesday: !!(profile.wednesday ?? profile.serviceWed),
  thursday: !!(profile.thursday ?? profile.serviceThu),
  friday: !!(profile.friday ?? profile.serviceFri),
  saturday: !!(profile.saturday ?? profile.serviceSat),
  sunday: !!(profile.sunday ?? profile.serviceSun),
  direct: resolveDirect(profile),
  quoteAllowed: !!profile.quoteAllowed,
  threePlOriginCode: normalizeOptionalCode(profile.threePlOriginCode),
  threePlDestCode: normalizeOptionalCode(profile.threePlDestCode),
  extensionCode1: normalizeOptionalCode(profile.extensionCode1),
  extensionCode2: normalizeOptionalCode(profile.extensionCode2),
});

export const formatServiceDays = (profile: ZipRoutingProfile): string => {
  const days: Array<[ServiceDayKey, boolean | undefined]> = [
    ["monday", profile.monday ?? profile.serviceMon],
    ["tuesday", profile.tuesday ?? profile.serviceTue],
    ["wednesday", profile.wednesday ?? profile.serviceWed],
    ["thursday", profile.thursday ?? profile.serviceThu],
    ["friday", profile.friday ?? profile.serviceFri],
    ["saturday", profile.saturday ?? profile.serviceSat],
    ["sunday", profile.sunday ?? profile.serviceSun],
  ];
  const open = days.filter(([, on]) => !!on).map(([day]) => SERVICE_DAY_LABELS[day]);
  return open.length ? open.join(" ") : "—";
};

export const SERVICE_DAYS_REQUIRED_MESSAGE = "At least one service day is required";

export const hasAtLeastOneServiceDay = (
  values: Pick<ZipRoutingProfileFormModel, ServiceDayKey>
): boolean => SERVICE_DAY_KEYS.some((day) => !!values[day]);

const hasRequiredServiceDays = function (this: any) {
  const parent = this.parent as ZipRoutingProfileFormModel | undefined;
  if (!parent) return true;
  return hasAtLeastOneServiceDay(parent);
};

export const getZipRoutingProfileValidation = (): Yup.ObjectSchema<ZipRoutingProfileFormModel> =>
  Yup.object({
    id: Yup.string().optional(),
    zipCode: Yup.string()
      .matches(/^\d{5}$/, "ZIP must be 5 digits")
      .required(validationMessages.required),
    state: Yup.string().required(validationMessages.required),
    city: Yup.string().max(64, "The value must be no more than 64 characters").default(""),
    terminalNumber: Yup.number()
      .nullable()
      .when("coverageStatus", {
        is: "SERVICED",
        then: Yup.number()
          .nullable()
          .required("Terminal is required")
          .typeError("Terminal is required"),
        otherwise: Yup.number().nullable().notRequired(),
      }),
    coverageStatus: Yup.mixed<CoverageStatus>()
      .oneOf(["SERVICED", "NOT_SERVICED"], "Coverage status is required")
      .required("Coverage status is required"),
    monday: Yup.boolean()
      .required()
      .test("at-least-one-service-day", SERVICE_DAYS_REQUIRED_MESSAGE, hasRequiredServiceDays),
    tuesday: Yup.boolean().required(),
    wednesday: Yup.boolean().required(),
    thursday: Yup.boolean().required(),
    friday: Yup.boolean().required(),
    saturday: Yup.boolean().required(),
    sunday: Yup.boolean().required(),
    direct: Yup.boolean().required(),
    quoteAllowed: Yup.boolean().required(),
    threePlOriginCode: Yup.string()
      .max(10)
      .oneOf(["", "D", "I"], "3PL origin must be blank, D (Direct), or I (Indirect)")
      .default(""),
    threePlDestCode: Yup.string()
      .max(10)
      .oneOf(["", "D", "I"], "3PL destination must be blank, D (Direct), or I (Indirect)")
      .default(""),
    extensionCode1: Yup.string()
      .max(10)
      .oneOf(["", "BGL"], "Routing code 1 must be blank or BGL")
      .default(""),
    extensionCode2: Yup.string()
      .max(10)
      .oneOf(
        ["", "FERRY", "REMOTE", "NEWYORK"],
        "Routing code 2 must be blank, FERRY, REMOTE, or NEWYORK"
      )
      .default(""),
  }).defined();
